import { csx, Divider, Text } from '@vtex/admin-ui'
import React from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useSuspenseFragment } from 'react-relay-offline'
import { FormattedMessage } from 'react-intl'

import { Price } from '../../../../core/components'
import { formatDifferencePercentageBetweenValues } from '../../../../core/utils/percentage'
import type { PaymentMethodInfoContent_PaymentOption$key } from './__generated__/PaymentMethodInfoContent_PaymentOption.graphql'
import { getBestInstallmentData } from './utils'

const paymentOptionFragment = graphql`
  fragment PaymentMethodInfoContent_PaymentOption on PaymentOption {
    total {
      amount
    }
    installments {
      edges {
        node {
          interestRate
          quantity
          total {
            amount
          }
          value {
            amount
          }
        }
      }
    }
    ...utils_PaymentOption
  }
`

export type PaymentMethodInfoContentProps = {
  paymentOptionRef: PaymentMethodInfoContent_PaymentOption$key
}

export const PaymentMethodInfoContent = ({
  paymentOptionRef,
}: PaymentMethodInfoContentProps) => {
  const paymentOption = useSuspenseFragment(
    paymentOptionFragment,
    paymentOptionRef
  )

  const bestInstallmentsData = getBestInstallmentData(paymentOption)

  return (
    <>
      <Text
        variant="display"
        className={csx({
          display: 'block',
        })}
      >
        <Price value={paymentOption.total.amount / 100} />
      </Text>
      <Text
        tone="secondary"
        className={csx({
          display: 'block',
        })}
      >
        {bestInstallmentsData.hasInterestRate ? (
          <Text variant="action2" tone="secondary">
            <FormattedMessage
              id="pdp.payment.best-installment-with-interest"
              values={{
                installmentsCount: bestInstallmentsData.installments.length,
              }}
            />
          </Text>
        ) : (
          <Text variant="action2" tone="positive">
            <FormattedMessage
              id="pdp.payment.best-installment-no-interest"
              values={{
                installmentsCount: bestInstallmentsData.installments.length,
              }}
            />
          </Text>
        )}
      </Text>
      <Divider className={csx({ marginY: '$space-8' })} />
      <Text variant="action1" className={csx({ display: 'block' })}>
        <FormattedMessage id="pdp.payment.installments-drawer.installments" />
      </Text>
      <Text
        tone="secondary"
        variant="action2"
        className={csx({
          display: 'block',
          marginTop: '$space-1',
          marginBottom: '$space-4',
        })}
      >
        <FormattedMessage id="pdp.payment.installments-drawer.installments-hint" />
      </Text>
      <div>
        {paymentOption.installments.edges.map((installment) => {
          const hasDiscount =
            installment.node.total.amount < paymentOption.total.amount

          const withoutInterest = installment.node.interestRate === 0

          return (
            <div
              key={installment.node.quantity}
              className={csx({
                display: 'grid',
                columnGap: '$space-6',
                gridTemplateColumns: '24px 1fr auto',
                alignItems: 'center',
                paddingY: '$space-4',
                ':not(:last-child)': {
                  borderBottom: '$neutral',
                },
              })}
            >
              <Text variant="action2">{`${installment.node.quantity}x`}</Text>
              <div>
                <Text variant="action2">
                  <Price value={installment.node.value.amount / 100} />
                </Text>
                <Text
                  variant="detail"
                  tone="positive"
                  className={csx({ marginLeft: '$space-2' })}
                >
                  {hasDiscount && (
                    <Text variant="detail" tone="positive">
                      <FormattedMessage
                        id="pdp.payment.installments-drawer.percentage-discount"
                        values={{
                          percentage: formatDifferencePercentageBetweenValues(
                            installment.node.total.amount,
                            paymentOption.total.amount,
                            0
                          ),
                        }}
                      />
                    </Text>
                  )}
                  {withoutInterest && (
                    <Text variant="detail" tone="positive">
                      <FormattedMessage id="interestFree" />
                    </Text>
                  )}
                </Text>
              </div>
              <Text variant="action2">
                <Price value={installment.node.total.amount / 100} />
              </Text>
            </div>
          )
        })}
      </div>
      <Divider
        className={csx({ marginTop: '$space-4', marginBottom: '$space-8' })}
      />
      <Text
        variant="action1"
        className={csx({
          display: 'block',
        })}
      >
        <FormattedMessage id="pdp.payment.installments-drawer.installment-details" />
      </Text>
      <Text
        tone="secondary"
        className={csx({
          display: 'block',
          marginTop: '$space-1',
        })}
      >
        <FormattedMessage id="pdp.payment.installments-drawer.installment-details-hint" />
      </Text>
    </>
  )
}
